import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { detectActives, findProductForIngredient } from "../lib/productMatch";
import { detectHairProducts, findHairProduct } from "../lib/hairProductMatch";

const TIER_COLORS = {
  budget: { bg: "#E8FDF0", color: "#1A5C35" },
  "mid-range": { bg: "#F5E8FD", color: "#6B3FA0" },
  midrange: { bg: "#F5E8FD", color: "#6B3FA0" },
  luxury: { bg: "#FDF5E8", color: "#7A4A0A" },
};

const CATEGORY_EMOJI = {
  "Leave-in": "💧",
  "Leave-in Spray": "💦",
  "Curl Cream": "🌀",
  "Hair Butter": "🧈",
  "Hair Oil": "🪔",
  "Scalp Oil": "🌿",
  "Scalp Treatment": "🌱",
  Shampoo: "🧴",
  Conditioner: "🧴",
  "Deep Conditioner": "🥥",
  "Co-Wash": "🫧",
  "Edge Control": "✨",
  Gel: "💎",
  "Styling Mousse": "☁️",
  Treatment: "🔬",
  "Treatment Spray": "🌬️",
  Detangler: "🪮",
};

// Inline chips that appear under any text containing recognised actives.
// Each chip shows: brand · product name · tier badge · Find dupe →
//
// Props:
//   text              — step text to scan
//   limit             — max chips
//   testIdPrefix      — for data-testid
//   mode              — "skin" (default) | "hair"
//   skinType          — user's skin_type (required in "skin" mode for strict match)
//   hairType          — required when mode==="hair" — filters by user's hair_type
//   concerns          — optional — used to nudge product picks toward user's concerns
export default function ProductChips({
  text,
  limit = 2,
  testIdPrefix = "pchip",
  mode = "skin",
  skinType = null,
  hairType = null,
  concerns = [],
}) {
  const [items, setItems] = useState([]);

  const concernsKey = concerns.join("|");
  useEffect(() => {
    let alive = true;
    setItems([]);
    (async () => {
      if (mode === "hair") {
        const types = detectHairProducts(text).slice(0, limit);
        if (types.length === 0) return;
        const found = await Promise.all(
          types.map(async (t) => {
            const p = await findHairProduct(t.category, hairType, concerns);
            return p ? { label: t.label, product: p } : null;
          })
        );
        if (alive) setItems(found.filter(Boolean));
      } else {
        const actives = detectActives(text).slice(0, limit);
        if (actives.length === 0) return;
        const found = await Promise.all(
          actives.map(async (a) => {
            const p = await findProductForIngredient(a, skinType);
            return p ? { label: a, product: p } : null;
          })
        );
        if (alive) setItems(found.filter(Boolean));
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, limit, mode, skinType, hairType, concernsKey]);

  if (items.length === 0) return null;

  return (
    <div className="mt-3 space-y-2">
      {items.map(({ label, product }) => {
        const tone =
          TIER_COLORS[(product.price_tier || "").toLowerCase()] || TIER_COLORS.luxury;
        const dupeHref = `/app/dupes?q=${encodeURIComponent(
          `${product.brand} ${product.name}`
        )}`;
        const emoji =
          mode === "hair" ? CATEGORY_EMOJI[product.category] || "🧴" : "🧴";
        const testIdSlug = label.replace(/\s+/g, "-").toLowerCase();
        return (
          <Link
            to={dupeHref}
            key={label}
            data-testid={`${testIdPrefix}-${testIdSlug}`}
            className="flex items-center gap-3 bg-white rounded-2xl p-3 border border-[#f0e8e4] hover:border-rose/40 transition-colors"
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-base shrink-0"
              style={{ background: mode === "hair" ? "#FDF1E2" : "#FDE8F0" }}
            >
              {emoji}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="text-[9px] uppercase tracking-wider font-medium leading-tight"
                style={{ color: mode === "hair" ? "#C9975A" : "#C9748A" }}
              >
                {label}
              </p>
              <p className="text-[10px] text-cocoa/55 truncate">{product.brand}</p>
              <p
                className="font-serif text-sm leading-tight truncate"
                style={{ color: "#2E1608" }}
              >
                {product.name}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <span
                className="text-[9px] font-medium px-2 py-0.5 rounded-full uppercase tracking-wider"
                style={{ background: tone.bg, color: tone.color }}
              >
                {product.price_tier}
              </span>
              <span className="text-[10px] text-rose font-medium inline-flex items-center gap-0.5">
                Find dupe <ArrowRight size={10} />
              </span>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
